import React, { useState, useEffect } from 'react';
import trTR from 'antd/lib/locale/tr_TR';
import { Calendar, Modal, Input, Form, TimePicker, message, Button, Card, Avatar, Spin, ConfigProvider, Badge } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import { useUser } from '../contexts/UserContext';
import { API_URL } from '../config/Config.js';
import axios from 'axios';
import { UserOutlined } from '@ant-design/icons';

dayjs.locale('tr');
dayjs.extend(utc);
dayjs.extend(timezone);

const EventModal = ({ open, onClose }) => {
  const { user } = useUser();
  const [value, setValue] = useState(dayjs());
  const [eventListModalOpen, setEventListModalOpen] = useState(false);
  const [eventFormModalOpen, setEventFormModalOpen] = useState(false);
  const [selectedDateForEvent, setSelectedDateForEvent] = useState(null);
  const [isAddEventButtonDisabled, setIsAddEventButtonDisabled] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form] = Form.useForm();

  // --- RESPONSIVE STATE (500px Sınırı) ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- CSS STYLES (Takvim Güzelleştirme) ---
  const calendarStyles = `
    .ant-picker-calendar-header {
      padding: 8px 0 !important;
    }
    .ant-picker-calendar-mode-switch {
      margin-left: 0 !important;
    }
    /* Mobilde takvim başlığını biraz küçültelim */
    @media (max-width: 900px) {
      .ant-picker-calendar-header .ant-select {
        font-size: 12px;
      }
      .ant-picker-content th {
        font-size: 12px;
        color: #888;
      }
    }
  `;
  // ---------------------------------------

  const [events, setEvents] = useState(() => {
    const storedEvents = localStorage.getItem('events');
    return storedEvents ? JSON.parse(storedEvents) : [];
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  useEffect(() => {
    localStorage.setItem('events', JSON.stringify(events));
  }, [events]);

  useEffect(() => {
    const storedEvents = localStorage.getItem('events');
    if (storedEvents) {
      setEvents(JSON.parse(storedEvents));
    }
    if (user.token) {
      fetchEvents(); // Always fetch to update events and load users
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token]);

  useEffect(() => {
    if (selectedDateForEvent) {
      const now = dayjs().subtract(100, 'minutes');
      const eventDate = selectedDateForEvent;
      const isValid = eventDate >= now;
      setIsAddEventButtonDisabled(!isValid);
    }
  }, [selectedDateForEvent]);

  const fetchEvents = async () => {
    setLoading(true);
    axios.defaults.headers.common['Authorization'] = "Bearer " + user.token;

    try {
      const userResponse = await axios.get(API_URL + "Users");
      if (Array.isArray(userResponse.data)) {
        setUsers(userResponse.data);
      }

      const eventResponse = await axios.get(API_URL + "Api/Events");
      if (eventResponse && eventResponse.data.success && Array.isArray(eventResponse.data.data)) {
        setEvents(eventResponse.data.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserById = (id) => {
    return users.find(user => user.id === id) || {};
  };

  const onSelect = (newValue) => {
    setValue(newValue);
  };

  const onPanelChange = (newValue) => {
    setValue(newValue);
  };

  const onDateCellClick = (date) => {
    const now = dayjs();
    if (date.isBefore(now, 'day')) {
      setSelectedDateForEvent(date);
      setEventListModalOpen(true);
      setIsAddEventButtonDisabled(true);
      message.warning("Geçmiş tarihlerde etkinlik eklenemez.");
      return;
    }
    setSelectedDateForEvent(date);
    setEventListModalOpen(true);
    setIsAddEventButtonDisabled(false);
  };

  const handleSaveEvent = async () => {
    try {
      await form.validateFields();
      setSaving(true);

      const selectedDate = selectedDateForEvent.startOf('day');
      const selectedTime = form.getFieldValue('time');
      const dateStr = selectedDate.format('YYYY-MM-DD');

      const newEvent = {
        userId: user.id,
        title: form.getFieldValue('title'),
        description: form.getFieldValue('description'),
        createdDate: dayjs(`${dateStr}T${selectedTime.format('HH:mm')}`).toISOString()
      };

      const response = await axios.post(API_URL + "Api/Events", newEvent, {
        headers: { Authorization: "Bearer " + user.token }
      });

      if (response.data.success) {
        message.success('Etkinlik başarıyla eklendi!');
        setEventFormModalOpen(false);
        setEvents(prevEvents => [...prevEvents, response.data.data]);
      } else {
        message.error(`Etkinlik eklenirken hata: ${response.data.message}`);
      }
    } catch (error) {
      message.error(`Etkinlik kaydederken hata oluştu: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvent = async () => {
    try {
      await form.validateFields();
      setSaving(true);

      const selectedDate = selectedDateForEvent.startOf('day');
      const selectedTime = form.getFieldValue('time');
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const updatedEvent = {
        userId: user.id,
        title: form.getFieldValue('title'),
        description: form.getFieldValue('description'),
        createdDate: dayjs(`${dateStr}T${selectedTime.format('HH:mm')}`).toISOString()
      };

      const response = await axios.put(`${API_URL}Api/Events/${selectedEvent.id}`, updatedEvent, {
        headers: { Authorization: "Bearer " + user.token }
      });

      if (response.data.success) {
        message.success('Etkinlik başarıyla güncellendi!');
        setEventFormModalOpen(false);
        setEvents(prevEvents =>
          prevEvents.map(ev => ev.id === selectedEvent.id ? response.data.data : ev)
        );
      } else {
        message.error(`Etkinlik güncellenirken hata: ${response.data.message}`);
      }
    } catch (error) {
      message.error(`Etkinlik güncellenirken hata oluştu: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = (eventToDelete) => {
    Modal.confirm({
      title: 'Emin misiniz?',
      content: 'Bu etkinliği silmek istediğinizden emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      zIndex: 2000,
      onOk: async () => {
        try {
          const response = await axios.delete(`${API_URL}Api/Events/${eventToDelete.id}`, {
            headers: { Authorization: "Bearer " + user.token }
          });

          if (response.data.success) {
            message.success('Etkinlik silindi!');
            setEvents(prevEvents => prevEvents.filter(ev => ev.id !== eventToDelete.id));
          } else {
            message.error('Etkinlik silinemedi!');
          }
        } catch {
          message.error('Silme sırasında hata oluştu!');
        }
      },
    });
  };

  // --- YENİ GÖRSEL TASARIM (Full Cell Render) ---
  const fullCellRender = (current) => {
    const dateStr = current.format('YYYY-MM-DD');
    const eventsForDate = events.filter(ev => dayjs(ev.createdDate).format('YYYY-MM-DD') === dateStr);

    // Durumları Kontrol Et
    const isPast = current.isBefore(dayjs(), 'day');
    const isToday = current.isSame(dayjs(), 'day');
    // Takvimdeki "seçili" tarih ile bu hücrenin tarihi aynı mı?
    const isSelected = value && current.isSame(value, 'day');
    // Bu ayın günü mü? (Takvimde bazen önceki/sonraki ayın günleri silik görünür)
    const isCurrentMonth = current.month() === value.month();

    // -- STİL AYARLARI --
    const baseStyle = {
      width: '100%',
      height: isMobile ? '50px' : '80px', // Masaüstünde daha uzun, mobilde kareye yakın
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      borderRadius: '8px',
      transition: 'all 0.2s',
      cursor: 'pointer',
      padding: '4px',
      position: 'relative',
      backgroundColor: isSelected ? '#e6f7ff' : 'transparent', // Seçiliyse açık mavi zemin
      border: isToday ? '1px solid #1890ff' : '1px solid transparent', // Bugünse mavi çerçeve
      opacity: isCurrentMonth ? 1 : 0.4, // Başka ayın günleri silik
    };

    // Gün Numarası Stili
    const numberStyle = {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: isToday || isSelected ? 'bold' : 'normal',
      color: isSelected ? '#1890ff' : (isPast ? '#999' : '#333'),
      width: '28px',
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
      backgroundColor: isSelected ? '#1890ff' : 'transparent', // Seçiliyse numara arkası mavi
      color: isSelected ? '#fff' : (isPast ? '#bbb' : '#333'), // Seçiliyse yazı beyaz
      marginBottom: '2px',
    };

    return (
      <div
        className="custom-cell"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation(); // Event bubbling engelle
          onSelect(current); // Önce takvim state'ini güncelle
          onDateCellClick(current); // Sonra modal açma mantığını çalıştır
        }}
        style={baseStyle}
      // Hover efekti için inline style yerine CSS class kullanımı daha iyi ama
      // hızlı çözüm için onMouseEnter/Leave kullanılabilir veya CSS dosyasına güvenilir.
      // Burada basitlik için style prop kullanıyoruz.
      >
        <div style={numberStyle}>
          {current.date()}
        </div>

        {/* Etkinlik Göstergesi */}
        {eventsForDate.length > 0 && (
          <div style={{ marginTop: 'auto', marginBottom: '2px' }}>
            {isMobile ? (
              // MOBİL GÖRÜNÜM: Sadece nokta
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: isPast ? '#ccc' : '#52c41a', // Geçmişse gri, değilse yeşil
                borderRadius: '50%',
              }} />
            ) : (
              // MASAÜSTÜ GÖRÜNÜM: Badge veya Metin
              <Badge
                color={isPast ? '#ccc' : '#52c41a'}
                text={
                  <span style={{ fontSize: '11px', color: '#666' }}>
                    {eventsForDate.length} Etk.
                  </span>
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const sortedEvents = events
    .filter(ev => dayjs(ev.createdDate).format('YYYY-MM-DD') === selectedDateForEvent?.format('YYYY-MM-DD'))
    .sort((a, b) => dayjs(a.createdDate).isBefore(dayjs(b.createdDate)) ? -1 : 1);

  return (
    <>
      <style>{calendarStyles}</style>
      <ConfigProvider locale={trTR}>
        {/* 1. Takvim Modalı */}
        <Modal
          centered
          title="Etkinlik Takvimi"
          open={open}
          onCancel={onClose}
          footer={null}
          width={isMobile ? '95%' : 800}
          style={isMobile ? { top: 10, padding: 0 } : {}}
          bodyStyle={{ padding: isMobile ? '10px' : '20px' }} // Padding düzeltildi
        >
          <Spin spinning={loading}>
            <div style={{
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)' // Hafif gölge
            }}>
              <Calendar
                fullscreen={!isMobile}
                value={value}
                onSelect={onSelect}
                onPanelChange={onPanelChange}
                // dateCellRender YERİNE fullCellRender kullanıyoruz
                fullCellRender={fullCellRender}
              />
            </div>
          </Spin>
        </Modal>

        {/* 2. Gün Etkinlikleri Listesi Modalı */}
        <Modal
          centered
          title={
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
              Etkinlikler - {selectedDateForEvent?.format('DD.MM.YYYY')}
            </span>
          }
          open={eventListModalOpen}
          onCancel={() => setEventListModalOpen(false)}
          footer={[
            <Button
              key="add"
              type="primary"
              size={isMobile ? 'middle' : 'large'}
              style={{ width: isMobile ? '100%' : 'auto' }}
              onClick={() => {
                form.resetFields();
                setSelectedEvent(null);
                setEventFormModalOpen(true);
              }}
              disabled={isAddEventButtonDisabled}
            >
              + Yeni Etkinlik
            </Button>
          ]}
          width={isMobile ? '95%' : 900}
          bodyStyle={{
            padding: isMobile ? '10px' : '20px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
        >
          <Spin spinning={loading}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {sortedEvents.length === 0 && (
                <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <div style={{ fontSize: '40px', opacity: 0.2 }}>📅</div>
                  <div>Bu tarihte planlanmış bir etkinlik yok.</div>
                </div>
              )}
              {sortedEvents.map((ev, idx) => {
                const creator = getUserById(ev.userId);
                const isPast = dayjs(ev.createdDate).isBefore(dayjs());
                const isFuture = dayjs(ev.createdDate).isAfter(dayjs());
                const cardBackground = isFuture ? '#f0faff' : isPast ? '#f9f9f9' : '#ffffff';
                const borderColor = isFuture ? '#d6e4ff' : '#f0f0f0';

                return (
                  <Card
                    key={idx}
                    style={{
                      width: isMobile ? '100%' : 'calc(50% - 8px)',
                      textAlign: 'left',
                      backgroundColor: cardBackground,
                      borderRadius: 12, // Daha yuvarlak kartlar
                      border: `1px solid ${borderColor}`,
                      boxShadow: 'none', // Gölge yerine border tercih edildi daha temiz görünüm için
                      marginBottom: 8,
                    }}
                    bodyStyle={{ padding: isMobile ? '12px' : '20px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <h3 style={{ fontSize: isMobile ? 14 : 16, fontWeight: 'bold', margin: 0, color: '#333' }}>
                        {ev.title}
                      </h3>
                      <span style={{
                        fontSize: 11,
                        color: isPast ? '#999' : '#1890ff',
                        backgroundColor: isPast ? '#eee' : '#e6f7ff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap',
                        marginLeft: 8
                      }}>
                        {isPast ? "Tamamlandı" : dayjs(ev.createdDate).format("HH:mm")}
                      </span>
                    </div>

                    <div style={{
                      backgroundColor: 'rgba(255,255,255,0.6)',
                      padding: '10px',
                      borderRadius: 8,
                      fontSize: 13,
                      color: '#555',
                      lineHeight: 1.5,
                      marginBottom: 12,
                      maxHeight: '150px',
                      overflowY: 'auto',
                    }}>
                      {ev.description}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#888' }}>
                        {creator && (
                          <>
                            <Avatar size={24} src={creator.url} icon={<UserOutlined />} style={{ border: '1px solid #ddd' }} />
                            {!isMobile && <span style={{ fontWeight: 500 }}>{creator.name} {creator.surname}</span>}
                          </>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        {creator.id === user.id && !isPast && (
                          <Button
                            type="default"
                            size="small"
                            onClick={() => {
                              setSelectedEvent(ev);
                              form.setFieldsValue({
                                title: ev.title,
                                description: ev.description,
                                time: dayjs(ev.createdDate)
                              });
                              setEventFormModalOpen(true);
                            }}
                          >
                            Düzenle
                          </Button>
                        )}
                        {creator.id === user.id && (
                          <Button
                            danger
                            type="text"
                            size="small"
                            onClick={() => handleDeleteEvent(ev)}
                          >
                            Sil
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Spin>
        </Modal>

        {/* 3. Etkinlik Ekleme/Düzenleme Formu Modalı */}
        <Modal
          title={selectedEvent ? "Etkinliği Düzenle" : "Yeni Etkinlik"}
          open={eventFormModalOpen}
          onCancel={() => setEventFormModalOpen(false)}
          onOk={selectedEvent ? handleUpdateEvent : handleSaveEvent}
          okText="Kaydet"
          cancelText="İptal"
          confirmLoading={saving}
          width={isMobile ? '95%' : 600}
          centered
        >
          <Form form={form} layout="vertical" initialValues={{ time: dayjs() }}>
            <Form.Item label="Başlık" name="title" rules={[{ required: true, message: 'Lütfen başlık girin!' }]}>
              <Input placeholder="Toplantı, Doğum Günü vb." size="large" />
            </Form.Item>

            <Form.Item label="Açıklama" name="description" rules={[{ required: true, message: 'Lütfen açıklama girin!' }]}>
              <Input.TextArea rows={isMobile ? 3 : 4} placeholder="Detayları buraya yazın..." />
            </Form.Item>

            <Form.Item label="Saat" name="time" rules={[{ required: true, message: 'Lütfen saat seçin!' }]}>
              <TimePicker
                format="HH:mm"
                size="large"
                style={{ width: '100%' }}
                disabledHours={() => {
                  if (selectedDateForEvent && dayjs().isSame(selectedDateForEvent, 'day')) {
                    const currentHour = dayjs().hour();
                    return Array.from({ length: currentHour }, (_, index) => index);
                  }
                  return [];
                }}
                disabledMinutes={(hour) => {
                  if (selectedDateForEvent && dayjs().isSame(selectedDateForEvent, 'day')) {
                    const currentMinute = dayjs().minute();
                    if (hour === dayjs().hour()) {
                      return Array.from({ length: currentMinute }, (_, index) => index);
                    }
                  }
                  return [];
                }}
              />
            </Form.Item>
          </Form>
        </Modal>
      </ConfigProvider>
    </>
  );
};

export default EventModal;